/** キーボードショートカットの修飾キーとキー本体。 */
export interface KeyCombo {
	key: string;
	mod: boolean;
	ctrl: boolean;
	shift: boolean;
	alt: boolean;
}

/** ショートカットを判定するための UI 状態。 */
export interface ShortcutContext {
	isComposing: boolean;
	defaultPrevented: boolean;
	hasOpenDialog: boolean;
	inMonaco: boolean;
	inEditableField: boolean;
	inRunningTerminal: boolean;
}

/**
 * コマンド実行中のターミナルにフォーカスがある間、Ctrl 修飾 (メタなし) のキーはコマンドへ渡すため
 * グローバルショートカット処理を素通しする。Win/Linux の Ctrl+P 等が vim/シェルへ届く一方、
 * Mac の Cmd 修飾はアプリのショートカット用に残る。
 */
export function shouldBypassShortcutsForRunningTerminal(
	context: ShortcutContext,
	event: { ctrlKey: boolean; metaKey: boolean },
): boolean {
	return context.inRunningTerminal && event.ctrlKey && !event.metaKey;
}

/** ショートカットの宣言。 */
export interface ShortcutDef {
	id: string;
	combo: KeyCombo;
	description: string;
	allowWhileEditing?: boolean;
	/**
	 * Monaco へフォーカスがあっても実行を許可する。inEditableField (Monaco の隠し
	 * テキストエリアも一致する) の判定より優先されるため、allowWhileEditing とは独立して働く。
	 */
	allowInMonaco?: boolean;
	/**
	 * enabled() や shouldSkipShortcut の判定結果に関わらず、コンボが一致した時点で
	 * 常に既定動作を止める（例: ブラウザの印刷ダイアログの抑止）。
	 */
	alwaysPreventDefault?: boolean;
	enabled?: () => boolean;
	run?: () => void;
}

const MODIFIER_TOKENS = new Set(["mod", "ctrl", "shift", "alt"]);

/** 文字列 DSL のキーコンボをショートカット定義へ変換する。 */
export function parseCombo(value: string): KeyCombo {
	const invalidComboMessage = `不正なキーコンボです: ${value}`;

	const tokens = value.toLowerCase().split("+");
	if (tokens.length < 2 || tokens.some((token) => token.length === 0)) {
		throw new Error(invalidComboMessage);
	}

	const modifiers = new Set<string>();
	let key: string | null = null;
	for (const token of tokens) {
		if (MODIFIER_TOKENS.has(token)) {
			if (modifiers.has(token)) throw new Error(invalidComboMessage);
			modifiers.add(token);
			continue;
		}
		if (key !== null) throw new Error(invalidComboMessage);
		key = token;
	}

	if (key === null || modifiers.size === 0) throw new Error(invalidComboMessage);
	return {
		key,
		mod: modifiers.has("mod"),
		ctrl: modifiers.has("ctrl"),
		shift: modifiers.has("shift"),
		alt: modifiers.has("alt"),
	};
}

/** イベントがキーコンボと完全に一致するかを返す。 */
export function matchesCombo(event: KeyboardEvent, combo: KeyCombo, isMac: boolean): boolean {
	const expectsMeta = combo.mod && isMac;
	const expectsCtrl = combo.ctrl || (combo.mod && !isMac);
	return (
		event.key.toLowerCase() === combo.key &&
		event.metaKey === expectsMeta &&
		event.ctrlKey === expectsCtrl &&
		event.shiftKey === combo.shift &&
		event.altKey === combo.alt
	);
}

/** 現在のフォーカス状態ではショートカットを実行しないかを返す。 */
export function shouldSkipShortcut(context: ShortcutContext, shortcut: ShortcutDef): boolean {
	if (context.isComposing) return true;
	if (context.defaultPrevented) return true;
	if (context.hasOpenDialog) return true;
	// Monaco の隠しテキストエリアは inEditableField にも一致するため、inMonaco を先に判定する。
	if (context.inMonaco) return !shortcut.allowInMonaco;
	return context.inEditableField && !shortcut.allowWhileEditing;
}

/** resolveShortcut の判定結果。 */
export interface ShortcutResolution {
	/** 実行すべきショートカット (無ければ null)。 */
	shortcut: ShortcutDef | null;
	/**
	 * shortcut が null でも、alwaysPreventDefault を指定したショートカットのコンボに
	 * 一致したためブラウザの既定動作は止めるべきかどうか。
	 */
	forcePreventDefault: boolean;
}

/**
 * イベントに対して実行すべき最初のショートカットと、既定動作を強制的に止めるべきかを
 * 1回の走査で判定する。
 */
export function resolveShortcut(
	shortcuts: readonly ShortcutDef[],
	event: KeyboardEvent,
	context: ShortcutContext,
	isMac: boolean,
): ShortcutResolution {
	let forcePreventDefault = false;
	for (const shortcut of shortcuts) {
		if (!matchesCombo(event, shortcut.combo, isMac)) continue;
		if (shortcut.alwaysPreventDefault) forcePreventDefault = true;
		if (!shortcut.run || (shortcut.enabled && !shortcut.enabled())) continue;
		if (shouldSkipShortcut(context, shortcut)) continue;
		return { shortcut, forcePreventDefault: true };
	}
	return { shortcut: null, forcePreventDefault };
}

/** プラットフォームに合わせたキーコンボの表示文字列を返す。 */
export function formatCombo(combo: KeyCombo, isMac: boolean): string {
	const key = combo.key.toUpperCase();
	if (isMac) {
		return `${combo.ctrl ? "⌃" : ""}${combo.alt ? "⌥" : ""}${combo.shift ? "⇧" : ""}${combo.mod ? "⌘" : ""}${key}`;
	}

	const modifiers = [
		combo.mod || combo.ctrl ? "Ctrl" : null,
		combo.alt ? "Alt" : null,
		combo.shift ? "Shift" : null,
	].filter((modifier): modifier is string => modifier !== null);
	return [...modifiers, key].join("+");
}

/** 指定したプラットフォームが macOS かを返す。 */
export function isMacPlatform(platform?: string): boolean {
	const value = platform ?? globalThis.navigator?.platform ?? "";
	return value.toLowerCase().includes("mac");
}
